export type DocumentPreviewKind = "pdf" | "sheet" | "csv" | "word" | "none"

const EXTENSION_KIND: Record<string, DocumentPreviewKind> = {
	pdf: "pdf",
	xlsx: "sheet",
	xlsm: "sheet",
	xls: "sheet",
	csv: "csv",
	tsv: "csv",
	docx: "word",
}

const MIME_KIND: Record<string, DocumentPreviewKind> = {
	"application/pdf": "pdf",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "sheet",
	"application/vnd.ms-excel": "sheet",
	"application/vnd.ms-excel.sheet.macroenabled.12": "sheet",
	"text/csv": "csv",
	"text/tab-separated-values": "csv",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
		"word",
}

export const PREVIEWABLE_EXTENSIONS = Object.keys(EXTENSION_KIND)

export const PREVIEWABLE_MIME_TYPES = Object.keys(MIME_KIND)

/**
 * Which preview a file gets. The extension wins, because a browser upload often
 * reports an empty or generic type for an Office file while the name is right.
 */
export function documentPreviewKind(file: {
	fileName?: string | null
	mimeType?: string | null
}): DocumentPreviewKind {
	const extension = (file.fileName ?? "").split(".").pop()?.toLowerCase() ?? ""
	const byExtension = EXTENSION_KIND[extension]
	if (byExtension) return byExtension

	const mime = (file.mimeType ?? "").split(";")[0]?.trim().toLowerCase() ?? ""
	return MIME_KIND[mime] ?? "none"
}

export function isPreviewableDocument(file: {
	fileName?: string | null
	mimeType?: string | null
}): boolean {
	return documentPreviewKind(file) !== "none"
}

/** Human size for the card, so a reader knows before opening a large file. */
export function formatFileSize(bytes: number | null | undefined): string {
	if (!bytes || bytes < 0 || !Number.isFinite(bytes)) return ""
	if (bytes < 1024) return `${bytes} B`
	const units = ["KB", "MB", "GB"]
	let size = bytes / 1024
	let unit = 0
	while (size >= 1024 && unit < units.length - 1) {
		size /= 1024
		unit++
	}
	return `${size >= 10 ? Math.round(size) : size.toFixed(1)} ${units[unit]}`
}

/** `accept` for a file input that takes media and every previewable document. */
export const MEDIA_UPLOAD_ACCEPT = [
	"image/jpg",
	"image/jpeg",
	"image/png",
	"image/gif",
	"image/webp",
	"video/*",
	...PREVIEWABLE_MIME_TYPES,
	...PREVIEWABLE_EXTENSIONS.map((extension) => `.${extension}`),
].join(",")
